import {
  filter,
  find,
  first,
  groupBy,
  includes,
  orderBy,
  pick,
  reduce,
  sumBy,
  take,
} from "lodash";
import { Person } from "@/features/people/graphql/generated";
import { Score, Dimension } from "@/shared/services/api/graphql/generated";
import { useDictionary } from "@/shared/hooks/content/useDictionary";
import {
  DimensionsFilterType,
  useDimensions,
} from "@/shared/hooks/content/useDimensions";
import { getAlignment } from "@/shared/utils/selectors/getAlignment";
import { AssessmentSurveyType } from "@/shared/constants/assessmentSurveys";
import {
  getIsCompetenciesDimension,
  getNormalizedDimensionGroup,
} from "@/shared/utils/selectors/getDimensionTypes";
import { DimensionContext } from "@/types/data";
import { useTranslation } from "react-i18next";

export interface Extreme {
  title?: string;
  description?: string;
}

export interface PersonScore extends Score {
  key: string;
  value: number;
  personId?: number;
  person?: Person;
}

export interface DimensionWithScores {
  id: number;
  title: string;
  code: string;
  group: string;
  type?: AssessmentSurveyType;
  group_title?: string;
  parent?: string;
  parentId?: number | null;
  description: string;
  peopleScores: PersonScore[];
  averageScore: Score;
  score: Score;
  textsScore: Score;
  alignment?: { [key: string]: any };
  extremes?: Extreme[];
  science?: string;
  keywords?: string[];
  allScores: Score[];
  dimension: Dimension;
}

const getObjectType = (dimensionFilter?: DimensionsFilterType) => {
  if (dimensionFilter?.personal) {
    return "viewer";
  }

  if (dimensionFilter?.people?.length && dimensionFilter.team_id) {
    return "in_team";
  }

  if (dimensionFilter?.team_id) {
    return "team";
  }

  if (dimensionFilter?.people) {
    return "people";
  }
};

export const getTopStrengthDimensions = (
  dimension: DimensionWithScores,
  subDimensions: DimensionWithScores[],
  context: DimensionContext
) => {
  // For competencies and values we return current dim
  if (
    subDimensions.length === 0 &&
    includes(
      [
        AssessmentSurveyType.SHWARTZ_VALUES,
        AssessmentSurveyType.WISNIO_COMPETENCIES,
        AssessmentSurveyType.AP_COMPETENCIES,
        AssessmentSurveyType.MATCH_V_V2,
      ],
      dimension.type
    )
  ) {
    return [dimension];
  }

  // https://assessio.atlassian.net/wiki/x/AYDLq
  const strengthDims = take(
    orderBy(
      subDimensions,
      (d) => {
        if (context === "user_in_team" || context === "candidate") {
          return Math.abs((d.score?.value || 0) - (d.averageScore?.value || 0));
        } else {
          return Math.abs(50 - (d.textsScore?.value || 0));
        }
      },
      "desc"
    ),
    2
  );

  return strengthDims;
};

/**
 *
 * @param dimensionFilter - The dimension filter to get the dimensions from the database
 * @param peopleMap - The people to map with the scores
 * @returns
 */
export const useDimensionsWithScores = (
  dimensionFilter?: DimensionsFilterType,
  peopleMap?: Person[]
) => {
  const peopleProfiles: Person[] = [...(peopleMap || [])].filter(
    (p) => !!p?.id
  );

  const { t } = useTranslation();

  const { dimensions, loading } = useDimensions(dimensionFilter);

  const { getTerm, loading: loadingDictionary } = useDictionary();

  const objectType = getObjectType(dimensionFilter);

  const getDimensionExtremes = (dimId: number, group?: string) => {
    const isValues = group === "values";
    const minDescription = getTerm(dimId, "scale_description", "low");
    const minLabel = isValues
      ? "Less important"
      : getTerm(dimId, "label", "low");
    const maxDescription = getTerm(dimId, "scale_description", "high");
    const maxLabel = isValues
      ? "Very important"
      : getTerm(dimId, "label", "high");

    if (!minLabel || !maxLabel) {
      return [];
    }

    return [
      {
        title: minLabel,
        description: minDescription,
      },
      {
        title: maxLabel,
        description: maxDescription,
      },
    ];
  };

  const getDimensionPeopleScores = (dimension: Dimension) => {
    const scoreObjectType = "person";
    const scores = filter(dimension.scores || [], {
      obj_type: scoreObjectType,
      key: "score",
    });

    return scores.map((score) => {
      const person: Person =
        find(peopleProfiles, (person) => person?.id === score?.obj_id) || {};

      return {
        key: score?.key,
        value: score?.value,
        personId: score?.obj_id,
        person: {
          ...person,
          avatar_url: person?.avatar,
        },
      };
    });
  };

  const getDimensionOverallScore = (
    dimension: Dimension,
    isTextScore?: boolean
  ) => {
    // `extremes_scores` is a helper entry, never the dimension's actual score.
    // Exclude it so score selection (including first()) can never land on it.
    const scores = filter(
      dimension.scores || [],
      (score) => score?.key !== "extremes_scores"
    );

    if (objectType === "viewer") {
      return first(scores) || {};
    }

    if (includes(["in_team", "candidate_in_team"], objectType) && isTextScore) {
      return (
        find(scores, { key: "candidate_in_team" }) ||
        find(scores, { key: "in_team" }) ||
        first(scores) || // If no in_team or candidate_in_team, return the first score. Happens when the team is added but no people in team
        {}
      );
    }

    const scoresPerType = groupBy(scores, (score) => score?.obj_type);

    if (scoresPerType?.team && objectType === "team") {
      return first(scoresPerType.team) || {};
    }

    // org member
    return (
      find(
        scoresPerType.person,
        (p) =>
          p?.obj_id === first(dimensionFilter?.people) &&
          !includes(["in_team", "candidate_in_team"], p?.key)
      ) || {}
    );
  };

  const getDimensionAverageScore = (dimension: Dimension) => {
    // Never let the average fall back to the `extremes_scores` helper entry.
    const scores = filter(
      dimension.scores || [],
      (score) => score?.key !== "extremes_scores"
    );

    if (objectType === "viewer") {
      return first(scores);
    }

    const scoresPerType = groupBy(scores, (score) => score?.obj_type);

    if (scoresPerType?.team) {
      return find(scoresPerType.team, { key: "mean" });
    }

    return {};
  };

  const getAlignmentObject = (
    dimension: Dimension,
    peopleScores: PersonScore[]
  ) => {
    const resourceType = dimension?.group;

    const isVisible =
      resourceType &&
      !getIsCompetenciesDimension(resourceType as AssessmentSurveyType) &&
      objectType !== "viewer";
    if (!isVisible) return undefined;

    const getAlignmentData = () => {
      if (objectType === "team") {
        const teamScore = find(dimension.scores, { key: "stdev" });
        return getAlignment(
          "team",
          teamScore?.value || 0,
          peopleScores,
          resourceType as any
        );
      }

      const personId = dimensionFilter?.people?.[0];
      const personScore = find(peopleScores, (s) => s?.person?.id === personId);
      if (!personId || !personScore) return undefined;

      return getAlignment(
        "individual",
        personScore.value,
        filter(peopleScores, (s) => s?.person?.id !== personId),
        resourceType as any
      );
    };

    const alignment = getAlignmentData();
    if (!alignment) return undefined;
    return {
      ...alignment,
      label: alignment.label ? t(alignment.label).toUpperCase() : undefined,
    };
  };

  const dimensionsWithScores = dimensions.map((dimension: any) => {
    const score = getDimensionOverallScore(dimension!);
    const peopleScores = getDimensionPeopleScores(dimension!);
    return {
      id: dimension?.dim_id,
      ...pick(dimension, ["code", "group", "type", "parent", "group_title"]),
      parentId: dimension?.parent_id,
      peopleScores,
      allScores: dimension?.scores,
      score,
      textsScore: getDimensionOverallScore(dimension!, true),
      averageScore: getDimensionAverageScore(dimension!),
      title: getTerm(dimension?.dim_id!, "name")?.replaceAll("_", " "),
      description: getTerm(dimension?.dim_id!, "description"),
      extremes: getDimensionExtremes(
        dimension?.dim_id!,
        dimension?.group || ""
      ),
      alignment: getAlignmentObject(dimension!, peopleScores as any),
      science: getTerm(dimension?.dim_id!, "science"),
      dimension, // Pass the original dimension object
    } as DimensionWithScores;
  });

  const dimensionsData = reduce(
    groupBy(dimensionsWithScores, "group"),
    (acc, groupDimensions, group) => {
      return {
        ...acc,
        [getNormalizedDimensionGroup(group as AssessmentSurveyType)]:
          groupDimensions,
      };
    },
    {} as { [key: string]: DimensionWithScores[] }
  );

  return {
    dimensionsData,
    dimensions: dimensionsWithScores,
    loading: loading || loadingDictionary,
    getDimensionPeopleScores,
  };
};
